import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { message, Card, ConfigProvider, theme } from 'antd';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';

import { MODULE_IDS } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';

import { getUnits, createUnit, updateUnit, deleteUnit } from '../../api/units.api';

/* ─────────────────────────────────────────────
   Light Theme Override Styles (Scoped to this page)
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-unit-master {
      /* Overriding global dark variables for this specific page */
      --bg: #f9fafb;
      --surface: #ffffff;
      --surface-2: #f3f4f6;
      --border: #e5e7eb;
      --border-hi: #d1d5db;
      --text-1: #111827;
      --text-2: #374151;
      --text-3: #6b7280;
      --accent: #f59e0b;
      --accent-dim: rgba(245, 158, 11, 0.15);
      --green: #10b981;
    }

    @keyframes rpFadeUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
    @keyframes rpSlideIn { from { transform:translateX(100%) } to { transform:translateX(0) } }
    @keyframes rpPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    @keyframes rpSpin { to { transform:rotate(360deg) } }

    .rp-live-dot { animation: rpPulse 2s ease-in-out infinite; }
    .rp-drawer-enter { animation: rpSlideIn 0.28s cubic-bezier(0.4,0,0.2,1); }

    .rp-row:hover { background: var(--surface-2) !important; }
    .rp-act-edit:hover  { border-color:#3b82f6 !important; color:#3b82f6 !important; background:rgba(59,130,246,0.1) !important; }
    .rp-act-del:hover   { border-color:#ef4444 !important; color:#ef4444 !important; background:rgba(239,68,68,0.1) !important; }
    .rp-btn-add:hover   { background:#e08d00 !important; }
    .rp-btn-primary:hover:not(:disabled) { background:#e08d00 !important; }
    .rp-btn-ghost:hover { border-color:var(--border-hi) !important; color:var(--text-1) !important; background:var(--surface-2) !important; }
    .rp-search-input:focus { border-color: var(--border-hi) !important; }
    .rp-form-input:focus  { border-color: var(--accent) !important; background: rgba(245,158,11,0.04) !important; }
    .rp-pg-btn-active { background: var(--accent-dim) !important; border-color: var(--accent) !important; color: var(--accent) !important; }
    .rp-pg-btn:hover:not(:disabled):not(.rp-pg-btn-active) { border-color: var(--border-hi) !important; color: var(--text-1) !important; }
    .rp-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .rp-spinner { display:inline-block; width:12px; height:12px; border:2px solid rgba(0,0,0,0.25); border-top-color:var(--text-1); border-radius:50%; animation:rpSpin 0.6s linear infinite; }
    
    /* Card Styles Tuning */
    .light-unit-master .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    }
    .light-unit-master .ant-card-head {
      border-bottom-color: var(--border);
      padding: 16px 24px;
      min-height: auto;
    }
    .light-unit-master .ant-card-body {
      padding: 24px;
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   Inline styles
───────────────────────────────────────────── */
const S = {
  page: { fontFamily: "'Barlow', sans-serif", padding: '4px 0', animation: 'rpFadeUp 0.3s ease' },

  pageTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 10, margin: 0 },
  titleDot: { width: 8, height: 8, background: 'var(--accent)', borderRadius: '50%', flexShrink: 0 },
  pageSubtitle: { fontSize: 12, color: 'var(--text-3)', marginTop: 4, letterSpacing: '0.3px', fontWeight: 'normal' },
  
  metaChip: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.5px' },
  liveDot: { width: 5, height: 5, background: 'var(--green)', borderRadius: '50%' },

  btnAdd: { display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px', transition: 'background var(--transition)' },

  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  searchWrap: { position: 'relative', flex: 1, maxWidth: 360 },
  searchIcon: { position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' },
  searchInput: { width: '100%', background: 'var(--surface)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '8px 34px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none' },
  searchClear: { position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 },
  resultCount: { fontSize: 12, color: 'var(--text-3)', marginLeft: 'auto' },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflowX: 'auto' },
  tableInner: { minWidth: 750 },
  tableHead: { display: 'grid', gridTemplateColumns: '50px minmax(160px, 1fr) 140px 140px 100px 140px 110px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' },
  th: { padding: '10px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' },
  tableBody: { maxHeight: 500, overflowY: 'auto' },
  tableRow: { display: 'grid', gridTemplateColumns: '50px minmax(160px, 1fr) 140px 140px 100px 140px 110px', borderBottom: '1px solid var(--border)', transition: 'background var(--transition)', cursor: 'default' },
  td: { padding: '12px 14px', fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', overflow: 'visible' },

  badge: { display: 'inline-flex', padding: '3px 8px', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, borderRadius: 2, letterSpacing: '0.5px' },
  badgeAccent: { display: 'inline-flex', padding: '3px 8px', background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: 11, fontWeight: 600, borderRadius: 2, letterSpacing: '0.5px' },

  actBtn: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', transition: 'all var(--transition)', flexShrink: 0 },
  empty: { padding: '60px 24px', textAlign: 'center' },
  emptyText: { fontSize: 14, color: 'var(--text-3)', marginTop: 8 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 },
  drawer: { position: 'fixed', right: 0, top: 0, bottom: 0, width: 420, background: 'var(--surface)', borderLeft: '1px solid var(--border)', zIndex: 401, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 15px rgba(0,0,0,0.05)' },
  drawerHeader: { padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0, background: 'var(--surface-2)' },
  drawerTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text-1)', margin: 0 },
  drawerSubtitle: { fontSize: 11, color: 'var(--text-3)', marginTop: 3 },
  drawerClose: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', flexShrink: 0 },
  drawerBody: { flex: 1, overflowY: 'auto', padding: 24 },
  drawerFooter: { padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexShrink: 0, background: 'var(--surface-2)' },

  formItem: { marginBottom: 20 },
  formLabelRow: { display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 },
  formLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-3)' },
  formRequired: { color: 'var(--accent)', fontSize: 13, lineHeight: 1 },
  formInput: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '9px 12px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none', transition: 'border-color var(--transition)' },
  formInputError: { borderColor: '#ef4444' },
  formHelp: { fontSize: 11, color: 'var(--text-3)', marginTop: 5, letterSpacing: '0.3px', lineHeight: 1.4 },
  formHelpHighlight: { color: 'var(--accent)', fontWeight: 600, fontSize: 12 },
  formError: { fontSize: 11, color: '#ef4444', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 },

  btnPrimary: { flex: 1, padding: '9px 16px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'background var(--transition)' },
  btnGhost: { padding: '9px 16px', background: 'none', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 500, color: 'var(--text-2)', cursor: 'pointer', transition: 'all var(--transition)' },

  delPop: { position: 'absolute', right: 36, top: -20, background: 'var(--surface)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: 12, minWidth: 210, zIndex: 50, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' },
  delPopTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 },
  delPopText: { fontSize: 11, color: 'var(--text-3)', marginBottom: 10, lineHeight: 1.5 },
  delPopBtns: { display: 'flex', gap: 6 },
  btnCancelSm: { flex: 1, padding: '5px 8px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 11, color: 'var(--text-2)', cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  btnDelSm: { flex: 1, padding: '5px 8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', fontSize: 11, color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontFamily: "'Barlow', sans-serif" },
};

/* ─────────────────────────────────────────────
   SVG Icons
───────────────────────────────────────────── */
const Icon = {
  Plus: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Search: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  X: ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Edit: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  Trash: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>,
  Check: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>,
  Warning: () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  Scale: () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
};

/* ─────────────────────────────────────────────
   Animated Styles
───────────────────────────────────────────── */
const PageStyles = () => (
  <style>{`
    /* Essential animations only, colors are handled by LightThemeStyles */
  `}</style>
);

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
const DeletePopover = ({ itemName, onConfirm, onCancel }) => (
  <div style={S.delPop} onClick={e => e.stopPropagation()}>
    <div style={S.delPopTitle}>Delete Unit?</div>
    <div style={S.delPopText}>"{itemName}"<br />This action cannot be undone.</div>
    <div style={S.delPopBtns}>
      <button style={S.btnCancelSm} onClick={onCancel}>Cancel</button>
      <button style={S.btnDelSm} onClick={onConfirm}>Delete</button>
    </div>
  </div>
);

const UnitRow = ({ unit, index, canEdit, canDelete, onEdit, onDelete, isDeleting }) => {
  const [showDelPop, setShowDelPop] = useState(false);
  const delRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (delRef.current && !delRef.current.contains(e.target)) setShowDelPop(false); };
    if (showDelPop) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDelPop]);

  return (
    <div className="rp-row" style={S.tableRow}>
      <div style={{ ...S.td, color: 'var(--text-3)', fontSize: 12 }}>{index}</div>
      <div style={{ ...S.td, color: 'var(--text-1)', fontWeight: 500 }}>{unit.unitName || '—'}</div>
      <div style={S.td}><span style={S.badge}>{unit.purchaseUnit}</span></div>
      <div style={S.td}><span style={S.badge}>{unit.consumptionUnit}</span></div>
      <div style={S.td}><span style={S.badgeAccent}>{unit.mFactor}</span></div>
      <div style={{ ...S.td, fontSize: 12, letterSpacing: '0.3px' }}>{formatDate(unit.createdOn)}</div>
      
      <div style={{ ...S.td, gap: 6, position: 'relative' }}>
        {canEdit && (
          <button className="rp-act-edit" style={S.actBtn} onClick={() => onEdit(unit)} title="Edit">
            <Icon.Edit />
          </button>
        )}
        {canDelete && (
          <div ref={delRef} style={{ position: 'relative' }}>
            <button className="rp-act-del" style={{ ...S.actBtn, ...(isDeleting ? { borderColor: '#ef4444', color: '#ef4444' } : {}) }} onClick={() => setShowDelPop(v => !v)} title="Delete">
              {isDeleting ? <span className="rp-spinner" style={{ borderColor: 'rgba(239,68,68,0.3)', borderTopColor: '#ef4444' }} /> : <Icon.Trash />}
            </button>
            {showDelPop && (
              <DeletePopover itemName={unit.purchaseUnit} onConfirm={() => { onDelete(unit.unitID); setShowDelPop(false); }} onCancel={() => setShowDelPop(false)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
const UnitDrawer = ({ open, onClose, onSave, editing, loading }) => {
  const drawerRef = useRef(null); // 🔴 NAYA: Drawer ko target karne ke liye ref

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: { unitName: '', purchaseUnit: '', consumptionUnit: '', mFactor: 1 },
  });

  const pUnit = watch('purchaseUnit') || 'P_UNIT';
  const cUnit = watch('consumptionUnit') || 'C_UNIT';
  const mFact = watch('mFactor') || '{factor}';

  useEffect(() => {
    if (open) {
      reset(editing
        ? { unitName: editing.unitName || '', purchaseUnit: editing.purchaseUnit || '', consumptionUnit: editing.consumptionUnit || '', mFactor: editing.mFactor || 1 }
        : { unitName: '', purchaseUnit: '', consumptionUnit: '', mFactor: 1 }
      );

      // 🔴 NAYA: Drawer khulte hi pehle input (Unit Name) par cursor lane ke liye
      setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input[name="unitName"]');
        if (firstInput) firstInput.focus();
      }, 150);
    }
  }, [open, editing, reset]);

  // 🔴 NAYA: Keyboard Navigation Logic (Enter-to-Next-Field)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (!drawerRef.current || !drawerRef.current.contains(e.target)) return;

      // Drawer ke andar ke saare inputs aur Save button dhoondo
      const focusable = drawerRef.current.querySelectorAll(
        'input:not([disabled]):not([readonly]), ' +
        'button.rp-btn-primary:not([disabled])'
      );
      
      const elementsArr = Array.from(focusable);
      const activeIndex = elementsArr.indexOf(document.activeElement);

      if (activeIndex === -1) return;

      // Agar 'Save Unit' button par Enter dabaya hai, toh form submit hone do
      if (document.activeElement.classList.contains('rp-btn-primary')) {
        return; 
      }

      // Default submit roko aur aage wale field par jump karo
      e.preventDefault(); 
      const nextIndex = activeIndex + 1;
      if (nextIndex < elementsArr.length) {
        const nextEl = elementsArr[nextIndex];
        nextEl.focus();
        if (nextEl.tagName === 'INPUT') {
          nextEl.select(); // Text select kar lo taaki overwrite karna aasan ho
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div style={S.overlay} onClick={onClose} />
      {/* 🔴 NAYA: Yahan ref={drawerRef} add kiya hai */}
      <div style={S.drawer} className="rp-drawer-enter" ref={drawerRef}>
        <div style={S.drawerHeader}>
          <div>
            <div style={S.drawerTitle}>{editing ? 'Edit Unit' : 'Add Unit'}</div>
            <div style={S.drawerSubtitle}>{editing ? 'Modify unit details below' : 'Define new measurement parameters'}</div>
          </div>
          <button style={S.drawerClose} className="rp-act-del" onClick={onClose}><Icon.X /></button>
        </div>

        <div style={S.drawerBody}>
          {/* Unit Name */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>Unit Name (Optional)</span></div>
            <Controller
              name="unitName" control={control}
              render={({ field }) => (
                <input {...field} className="rp-form-input" style={S.formInput} placeholder="e.g. Kilogram/Gram" autoComplete="off" />
              )}
            />
          </div>

          {/* Purchase Unit */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}>
              <span style={S.formLabel}>Purchase Unit</span>
              <span style={S.formRequired}>*</span>
            </div>
            <Controller
              name="purchaseUnit" control={control}
              rules={{ required: 'Purchase unit is required' }}
              render={({ field }) => (
                <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.purchaseUnit ? S.formInputError : {}) }} 
                  placeholder="e.g. KG" autoComplete="off" onChange={(e) => field.onChange(e.target.value.toUpperCase())} 
                />
              )}
            />
            {errors.purchaseUnit && <div style={S.formError}><Icon.Warning />{errors.purchaseUnit.message}</div>}
          </div>

          {/* Consumption Unit */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}>
              <span style={S.formLabel}>Consumption Unit</span>
              <span style={S.formRequired}>*</span>
            </div>
            <Controller
              name="consumptionUnit" control={control}
              rules={{ required: 'Consumption unit is required' }}
              render={({ field }) => (
                <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.consumptionUnit ? S.formInputError : {}) }} 
                  placeholder="e.g. GRM" autoComplete="off" onChange={(e) => field.onChange(e.target.value.toUpperCase())} 
                />
              )}
            />
            {errors.consumptionUnit && <div style={S.formError}><Icon.Warning />{errors.consumptionUnit.message}</div>}
          </div>

          {/* M-Factor */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}>
              <span style={S.formLabel}>M-Factor (Conversion)</span>
              <span style={S.formRequired}>*</span>
            </div>
            <Controller
              name="mFactor" control={control}
              rules={{ required: 'M-Factor is required', min: { value: 0.0001, message: 'Must be > 0' } }}
              render={({ field }) => (
                <input {...field} type="number" step="any" min="0.0001" className="rp-form-input" style={{ ...S.formInput, ...(errors.mFactor ? S.formInputError : {}) }} placeholder="e.g. 1000" />
              )}
            />
            {errors.mFactor ? (
              <div style={S.formError}><Icon.Warning />{errors.mFactor.message}</div>
            ) : (
              <div style={S.formHelp}>
                Formula: <span style={S.formHelpHighlight}>1 {pUnit} = {mFact} {cUnit}</span>
              </div>
            )}
          </div>
        </div>

        <div style={S.drawerFooter}>
          <button style={S.btnGhost} className="rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} className="rp-btn-primary" onClick={handleSubmit(onSave)} disabled={loading}>
            {loading ? <><span className="rp-spinner" />Saving...</> : <><Icon.Check />Save Unit</>}
          </button>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Main Page Component
───────────────────────────────────────────── */
const UnitMasterPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [searchText, setSearchText] = useState('');
  const searchRef = useRef(null);

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.RAW_MATERIAL);

  /* Queries */
  const { data: units = [], isLoading } = useQuery({
    queryKey: ['units', plantId, searchText],
    queryFn: () => getUnits(plantId, { search: searchText }),
    enabled: !!plantId,
  });

  const totalCount = units.length;

  /* Mutations */
  const createMutation = useMutation({
    mutationFn: (d) => createUnit(plantId, d),
    onSuccess: () => { message.success('Unit created successfully!'); queryClient.invalidateQueries({ queryKey: ['units'] }); closeDrawer(); },
    onError: (err) => message.error(err.message?.includes('exists') ? 'Unit already exists.' : err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (d) => updateUnit(plantId, editingUnit.unitID, d),
    onSuccess: () => { message.success('Unit updated successfully!'); queryClient.invalidateQueries({ queryKey: ['units'] }); closeDrawer(); },
    onError: (err) => message.error(err.message?.includes('exists') ? 'Unit already exists.' : err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (uid) => deleteUnit(plantId, uid),
    onSuccess: () => { message.success('Unit deleted successfully!'); queryClient.invalidateQueries({ queryKey: ['units'] }); },
    onError: (err) => message.error(err.message || 'Cannot delete unit. It might be in use.'),
  });

  /* Handlers */
  const openDrawer = (record = null) => { setEditingUnit(record); setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); setEditingUnit(null); };
  
  const onSave = (formData) => {
    const payload = { ...formData, mFactor: Number(formData.mFactor) };
    if (editingUnit) updateMutation.mutate(payload); else createMutation.mutate(payload);
  };



  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <PageStyles />
      <LightThemeStyles />
      <div className="light-unit-master" style={S.page}>

        <Card
          title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={S.pageTitle}>
                <span style={S.titleDot} />
                Unit Master
              </div>
              <div style={S.pageSubtitle}>Manage units of measurement and conversions</div>
            </div>
          }
          extra={
            canAdd && (
              <button className="rp-btn-add" style={S.btnAdd} onClick={() => openDrawer(null)}>
                <Icon.Plus /> Add Unit
              </button>
            )
          }
          styles={{ header: { padding: '20px 24px' } }}
        >
          
          {/* Toolbar */}
          <div style={S.toolbar}>
            <div style={S.searchWrap}>
              <span style={S.searchIcon}><Icon.Search /></span>
              <input ref={searchRef} className="rp-search-input" style={S.searchInput} placeholder="Search by unit name... (Ctrl+F)" value={searchText} onChange={e => setSearchText(e.target.value)} autoComplete="off" />
              {searchText && <button style={S.searchClear} onClick={() => setSearchText('')}><Icon.X size={11} /></button>}
            </div>
            {searchText && <span style={S.resultCount}>{totalCount} result{totalCount !== 1 ? 's' : ''}</span>}
          </div>

          {/* Table */}
          <div style={S.tableWrap}>
            <div style={S.tableInner}>
              <div style={S.tableHead}>
                {['#', 'Unit Name', 'Purchase Unit', 'Consumption Unit', 'M-Factor', 'Created On', 'Actions'].map(h => (
                  <div key={h} style={S.th}>{h}</div>
                ))}
              </div>
              
              <div style={S.tableBody}>
                {isLoading ? (
                  <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <span className="rp-spinner" style={{ width: 22, height: 22, borderWidth: 2, borderColor: 'var(--border-hi)', borderTopColor: 'var(--accent)' }} />
                  </div>
                ) : units.length === 0 ? (
                  <div style={S.empty}>
                    <div style={{ color: 'var(--text-3)', marginBottom: 8, display: 'flex', justifyContent: 'center' }}><Icon.Scale /></div>
                    <div style={S.emptyText}>{searchText ? 'No units found matching criteria.' : 'No units defined yet. Click "Add Unit" to get started.'}</div>
                  </div>
                ) : (
                  units.map((u, i) => (
                    <UnitRow
                      key={u.unitID} unit={u} index={i + 1}
                      canEdit={canEdit} canDelete={canDelete} onEdit={openDrawer}
                      onDelete={(uid) => deleteMutation.mutate(uid)}
                      isDeleting={deleteMutation.isPending && deleteMutation.variables === u.unitID}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Drawer */}
        <UnitDrawer
          open={drawerOpen} onClose={closeDrawer} onSave={onSave} editing={editingUnit} loading={isMutating}
        />
      </div>
    </ConfigProvider>
  );
};

export default UnitMasterPage;